import React from "react";

// Prerendered snapshots must ship fully visible markup — crawlers should never
// receive marketing copy stuck at opacity 0 below the fold.
const isPrerender =
  typeof window !== "undefined" && !!window.__PRERENDER_INJECTED;

/**
 * Scroll-triggered reveal. Adds `.is-visible` once the element enters the
 * viewport; the animation itself lives in styles/apple.css so it can be
 * reused by plain markup too.
 */
export default function Reveal({
  children,
  as: Component = "div",
  direction = "up",
  delay = 0,
  threshold = 0.16,
  once = true,
  className = "",
  style,
  ...rest
}) {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(isPrerender);

  React.useEffect(() => {
    if (isPrerender) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once]);

  return (
    <Component
      ref={ref}
      data-direction={direction}
      className={`app-reveal${visible ? " is-visible" : ""}${
        className ? ` ${className}` : ""
      }`}
      style={{ "--app-reveal-delay": `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Component>
  );
}
